import { chatStore$ } from "@/lib/ContactsMap"
import { cyrb64Hash } from "@/lib/crypto.utils"
import { ContactSchema, type TContact } from "@/schemas/contacts"
import { dynamoStore$ } from "@/stores/dynamo.v2.store"
import { usePowerDialerStore } from "@/stores/powerDialer/store"
import { fetchMapKeyData } from "@/sync/sync"
import type { IRegisterMapProps } from "@/sync/syncTypes"
import { batch, syncState, when } from "@legendapp/state"
import consola from "consola"

/**
 * Processes contact data and stores it in chatStore$.contacts
 * This function can be used both by sync handlers and direct fetch operations
 */
export function processAndStoreContactData(data: unknown, source: "sync" | "fetch" = "sync") {
  const fixedItem = fixContactObject(data)

  consola.log(`[TEST] Processing contact data from ${source}`, fixedItem)

  const parsedItem = ContactSchema.safeParse(fixedItem)
  if (!parsedItem.success) {
    consola.error(`Invalid contact data from ${source}, ignoring`, parsedItem.error)
    return false
  }

  parsedItem.data.Hash = cyrb64Hash(JSON.stringify(parsedItem.data))
  chatStore$.contacts[`${parsedItem.data.ContactPersonId}`].set(parsedItem.data)

  // Trigger Zustand cache recomputation if this contact is in the dialer list
  const contactId = `${parsedItem.data.ContactPersonId}`
  const store = usePowerDialerStore.getState()
  const allContactIds = [...store.cachedDialerContactIds, ...store.cachedPastCallContactIds]

  if (allContactIds.includes(contactId)) {
    store.recomputeDialerListCache()
  }

  return true
}

function fixContactObject(data: any) {
  // Try to get the id from the data
  const contactPersonId = data?.id ? `${data?.id}` : undefined

  // Get previous data from multiple sources (dynamo store and chat store)
  const dynamoPrevData: any | undefined = contactPersonId ? dynamoStore$.recentUserContacts.dataMap[contactPersonId].get() : {}
  const chatStorePrevData: any | undefined = contactPersonId ? chatStore$.contacts[contactPersonId].peek() : {}

  // Merge previous data, preferring chat store (from search) over dynamo store
  const contactPrevData = { ...dynamoPrevData, ...chatStorePrevData }

  return {
    ...contactPrevData,
    ContactPhoneNumber: data?.phoneNumber ?? contactPrevData?.ContactPhoneNumber,
    ContactEmail: data?.email ?? contactPrevData?.ContactEmail,
    ContactPersonId: (contactPersonId ?? data?.id) ? `${data.id}` : undefined,
    Note: data?.note ?? data?.lastNote?.data ?? contactPrevData?.Note,
    ContactRemoteJson: data?.remote ? JSON.stringify(data.remote) : contactPrevData?.ContactRemoteJson,
    SearchTerm: data?.searchTerm || contactPrevData?.SearchTerm || "",
    TTL: Number(data?.ttl ?? contactPrevData?.TTL ?? 0),
    ContactEmails: data?.emails ?? contactPrevData?.ContactEmails ?? [],
    ContactLastDisposition: data?.lastDisposition ?? contactPrevData?.ContactLastDisposition,
    LastTimestamp: Date.now(),
    ContactPhoneNumbers: data?.phoneNumbers ?? contactPrevData?.ContactPhoneNumbers ?? [],
    ContactCompanyName: data?.company ?? contactPrevData?.ContactCompanyName ?? "",
    AccountId: data?.accountId ?? contactPrevData?.AccountId ?? "",
    ContactDNC: data?.dnc ?? contactPrevData?.ContactDNC ?? false,
    ContactFullname: data?.fullName ?? data?.name ?? contactPrevData?.ContactFullname ?? "",
    Occurrences: data?.occurrences ?? contactPrevData?.Occurrences ?? 0,
    raw: data,
  } satisfies TContact
}

export const ContactsMapHandler = (name: string): IRegisterMapProps => ({
  name,
  onFailure: (error: unknown) => {
    consola.error(`Map ${name} failed`, error)
  },
  onRemoved: () => {
    consola.debug(`Map removed: ${name}`)
  },
  onItemAdded: (data: unknown, rawData: unknown) => {
    consola.log("[TEST] Item added to ${name}", data, rawData)
    processAndStoreContactData(data, "sync")
  },
  onItemRemoved: (data: unknown, rawData: unknown) => {
    consola.debug(`Item removed from ${name}`, data, rawData)
  },
  onItemUpdated: (data: unknown, rawData: unknown) => {
    consola.log("[TEST] Received an update for a contact", data)
    processAndStoreContactData(data, "sync")
  },
  onConnected: () => {
    const syncState$ = syncState(dynamoStore$)
    when(syncState$.isPersistLoaded).then(() => {
      // TODO: Remove this, we should fetch when we receive the list from dynamo
      const currentCachedContacts = dynamoStore$.recentUserContacts.sorted.get()

      batch(() => {
        for (const contact of currentCachedContacts) {
          // Fetch the contact from the map
          const contactUserID = contact.ContactPersonId

          fetchMapKeyData({
            key: contactUserID,
            map: "contacts",
          })
        }
      })
    })
  },
})
